/**
 * Every place a number, date or phone number becomes text.
 *
 * Centralised because §3 specifies two different money formats and getting them
 * the wrong way round is invisible in review: `KSh 1,234,567` on lists,
 * `KSh 1,234,567.89` on financial documents. Two functions rather than one with a
 * flag, so a caller has to say which it means.
 */

const NAIROBI = 'Africa/Nairobi'

/**
 * Month names, spelled out rather than taken from the locale.
 *
 * §3 asks for `DD MMM YYYY`. ICU 72 changed `en-GB` to abbreviate September as
 * "Sept" — four letters — so the same code renders a different string depending
 * on the browser's ICU version. A date format that varies by browser is not a
 * format.
 */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** `KSh 1,234,567` — list views, KPI tiles, dashboards. */
export function formatMoneyList(amount: number): string {
  return `KSh ${roundHalfUp(amount, 0).toLocaleString('en-KE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`
}

/** `KSh 1,234,567.89` — invoices, statements, receipts, anything that must foot. */
export function formatMoneyDocument(amount: number): string {
  return `KSh ${roundHalfUp(amount, 2).toLocaleString('en-KE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

/**
 * Away from zero, matching the server's Money type.
 *
 * Two traps, both of which this has to avoid:
 *
 * - `Math.round` breaks ties toward +∞, so -2.5 becomes -2 while the server's
 *   MidpointRounding.AwayFromZero gives -3. Hence the explicit sign handling.
 * - Multiplying to shift the decimal point reintroduces binary error: 1.005 * 100
 *   is 100.49999999999999 and rounds *down*, giving 1.00 for a value that should
 *   be 1.01. Adding Number.EPSILON does not fix it — epsilon is the gap next to
 *   1.0, and at a magnitude of 100 it is far too small to matter.
 *
 * Shifting through exponential notation avoids the multiply entirely: the string
 * "1.005e2" parses directly to 100.5.
 *
 * A total that differs by a cent between the screen and the printed invoice is a
 * support call, so this is worth the care.
 */
function roundHalfUp(value: number, decimals: number): number {
  if (!Number.isFinite(value)) return value

  const sign = value < 0 ? -1 : 1
  const abs = Math.abs(value)

  // Values already in exponential form cannot be re-shifted this way. No
  // realistic money value is, but falling back keeps the function total.
  if (abs.toString().includes('e')) {
    const factor = 10 ** decimals
    return (sign * Math.round(abs * factor)) / factor
  }

  const shifted = Number(`${abs}e${decimals}`)
  const rounded = Math.round(shifted)

  return sign * Number(`${rounded}e-${decimals}`)
}

/**
 * `09 Sep 2026`, always in Africa/Nairobi.
 *
 * Not the browser's timezone. A rep in another country must see the same business
 * date the shop did, or "today's sales" means two different things depending on
 * who is looking.
 */
export function formatDate(value: string | Date): string {
  const { day, month, year } = nairobiParts(value)
  return `${day} ${month} ${year}`
}

/** `09 Sep 2026, 15:42` in Africa/Nairobi. */
export function formatDateTime(value: string | Date): string {
  const { day, month, year, hour, minute } = nairobiParts(value)
  return `${day} ${month} ${year}, ${hour}:${minute}`
}

/**
 * `15:42` in Africa/Nairobi.
 *
 * For something that happens within the day — when a stock hold lapses, when a sale was parked. The
 * full date beside it on a till row is noise a cashier has to read past.
 */
export function formatTime(value: string | Date): string {
  const { hour, minute } = nairobiParts(value)
  return `${hour}:${minute}`
}

/**
 * The date, broken into parts, in Africa/Nairobi.
 *
 * Numeric month, then mapped through MONTHS — so the abbreviation is ours and
 * cannot shift under an ICU update. `hourCycle: 'h23'` because `hour12: false`
 * renders midnight as 24 in some ICU versions.
 */
function nairobiParts(value: string | Date): Record<string, string> {
  const date = typeof value === 'string' ? new Date(value) : value

  const parts = new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone: NAIROBI,
  }).formatToParts(date)

  const found: Record<string, string> = {}
  for (const part of parts) {
    if (part.type !== 'literal') found[part.type] = part.value
  }

  found.month = MONTHS[Number(found.month) - 1] ?? found.month
  return found
}

/** Stored `254712345678`, shown `0712 345 678`. */
/** The East African dialling codes a number may be stored under (D-267). */
const eastAfrican = ['254', '256', '255', '250', '257', '211', '252', '243']

export function formatPhone(msisdn: string | null | undefined): string {
  if (!msisdn) return ''
  const digits = msisdn.replace(/\D/g, '')

  if (digits.length === 12 && digits.startsWith('254')) {
    const national = digits.slice(3)
    return `0${national.slice(0, 3)} ${national.slice(3, 6)} ${national.slice(6)}`
  }

  // A neighbour's number keeps its code and never gains a leading zero: a Ugandan number shown as
  // 0712 345 678 looks Kenyan, and somebody would dial it that way.
  const code = eastAfrican.find((c) => digits.startsWith(c) && digits.length > c.length)
  if (code) {
    return `+${code} ${(digits.slice(code.length).match(/.{1,3}/g) ?? []).join(' ')}`
  }

  return msisdn
}

/** Who a message went to: a phone as people read it, an email address as it is. */
export function formatRecipient(recipient: string): string {
  return recipient.includes('@') ? recipient : formatPhone(recipient)
}

/** Initials for an avatar. "Diana P." becomes "DP". */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

/**
 * Today's business date in Africa/Nairobi, as `YYYY-MM-DD` — what a date input and
 * the API both take. The en-CA locale formats dates in exactly that order.
 */
export function todayInNairobi(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: NAIROBI, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date())
}
